<template>
    <div
        id="editor"
        class="col-sm-12 nopads"
    >
        <div
            v-if="documentUpdate"
            class="col-sm-12 editor-form"
        >
            <h2 class="editor-header">
                {{ documentUpdate.id > 0 ? (editable ? $t('common.edit') : $t('general_guides.file')) : $t('general_guides.add_new_guide') }}
            </h2>
            <div class="col-sm-12">
                <div class="nopads">
                    <span class="span-title">
                        {{ $t('general_guides.document_name') }}
                    </span>
                    <input
                        id="document-name"
                        v-model="documentUpdate.name"
                        :disabled="!editable && documentUpdate.id !== -1"
                        type="text"
                        class="form-control form-control-sm"
                    >
                    <span class="span-title">
                        {{ $t('general_guides.category') }}
                    </span>
                    <div class="input-group">
                        <select
                            id="sort-by-select"
                            v-model="category"
                            class="form-select form-select-sm mt-1"
                        >
                            <option :value="null">
                                {{ $t('general_guides.select_category') }}
                            </option>
                            <option
                                v-for="cat in categories"
                                :key="cat.value || cat.id"
                                :value="cat.value || cat.id"
                            >
                                {{ cat.text || cat.name }}
                            </option>
                        </select>

                        <button
                            type="button"
                            class="btn btn-success btn-sm mt-1"
                            @click="showNewCategory = true"
                        >
                            {{ $t('common.add') }}
                        </button>
                    </div>
                    <span class="span-title">
                        {{ $t('general_guides.document_description') }}
                    </span>
                    <input
                        id="document-description"
                        v-model="documentUpdate.description"
                        type="text"
                        class="form-control form-control-sm"
                        :disabled="!editable && documentUpdate.id !== -1"
                    >
                    <div v-if="documentUpdate.id === -1">
                        <span class="span-title">
                            {{ $t('general_guides.choose_file') }}
                        </span>
                        <span class="tip-light">
                            (.pdf, .doc, .docx, .jpg, .jpeg, .png)
                        </span>
                        <input
                            type="file"
                            class="form-control form-control-sm"
                            accept=".pdf, .doc, .docx, .jpg, .jpeg, .png"
                            :placeholder="$t('common.attachment_placeholder')"
                            @change="e => attachments[0] = e.target.files[0]"
                        >
                    </div>
                    <div
                        v-else
                    >
                        <div
                            v-if="documentUpdate.attachment"
                            class="mb-2"
                        >
                            <span class="span-title">{{ $t('general_guides.attachment') }}</span>
                            <div
                                class="pointer link mb-2"
                                @click="downloadAttachment(documentUpdate)"
                            >
                                {{ documentUpdate.attachment.file_original_name }}
                                <button
                                    class="btn btn-sm btn-primary ml-3"
                                    @click.stop="downloadAttachment(documentUpdate)"
                                >
                                    <i class="fa fa-download" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-sm-12 d-flex justify-content-end my-2">
                    <button
                        v-if="documentUpdate.id > 0 && editable"
                        class="btn btn-danger form-button"
                        :disabled="loading"
                        @click.stop="confirmDeleteSharedDocument(documentUpdate.id)"
                    >
                        {{ $t('common.remove') }}
                    </button>
                    <button
                        class="btn btn-secondary form-button"
                        @click.stop="$emit('close')"
                    >
                        {{ $t('common.cancel') }}
                    </button>
                    <button
                        v-if="editable || documentUpdate.id === -1"
                        class="btn btn-success form-button"
                        :disabled="loading"
                        @click.stop="validateInputData()"
                    >
                        {{ $t('common.save') }}
                    </button>
                </div>
            </div>
        </div>
        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
        <base-modal
            v-if="deletion"
            @ok="deleteSharedDocument(documentUpdate.id)"
            @close="deletion = false"
        >
            <div class="modal-body">
                <span>{{ $t('common.confirm_delete') }}</span>
            </div>
        </base-modal>
        <base-modal
            v-if="showNewCategory"
            @ok="submitCategory"
            @close="showNewCategory = false"
        >
            <div class="modal-body">
                <span>{{ $t('general_guides.category') }}</span>
                <input
                    v-model="newCategoryName"
                    type="text"
                    class="form-control form-control-sm"
                >
            </div>
        </base-modal>
    </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {attachmentHelper} from '../mixins/AttachmentMixin'
import BaseModal from "@/components/BaseModal";
import {inject} from "vue";

export default {
    name: "GeneralGuidesEditor",
    components: {BaseModal},
    mixins : [restApi, attachmentHelper],
    props: {
        editable: {
            type: Boolean,
            default: true
        },
        document: {
            type: Object,
            default: null
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data () {
        return {
            id: '',
            guideName: '',
            documentUpdate: null,
            status: '',
            loading: false,
            attachments: [],
            categories: [],
            category: null,
            deletion: false,
            showNewCategory: false,
            newCategoryName: null
        }
    },

    mounted() {
        this.documentUpdate = JSON.parse(JSON.stringify(this.document))
        if (this.documentUpdate.category) {
            this.category = this.documentUpdate.category.id
        }
        this.fetchCategories()
    },
    methods:{
        fetchCategories() {
            this.loading = true
            this.categories = []
            this.restFetch(this.sharedDocumentCategoryUrl, this.handleCategories)
        },
        handleCategories(response) {
            this.loading = false
            if (response && response.data) {
                response.data.forEach(cat => this.categories.push(
                    {
                        value: cat.id,
                        text: cat.name
                    }
                ))
            }
        },
        submitCategory() {
            if (!this.loading) {
                this.loading = true
                if (this.newCategoryName) {
                    this.restAdd(this.sharedDocumentCategoryUrl, {name: this.newCategoryName}, this.success, this.failed)
                }
            }
        },
        success() {
            this.loading = false
            this.fetchCategories()
        },
        failed() {
            this.loading = false
        },
        validateInputData() {
            this.loading = true
            if (this.category) {
                this.documentUpdate.category = {id: this.category}
            } else {
                this.documentUpdate.category = null
            }
            if (this.documentUpdate.name === '') {
                this.showAlert(this.$t('general_guides.document_name_missing'))
                this.loading = false
            }
            else if (this.documentUpdate.attachment === null && this.attachments.length === 0) {
                this.showAlert(this.$t('general_guides.attachment_missing'))
                this.loading = false
            } else if (this.documentUpdate.id === -1){
                let formData = new FormData()
                this.attachments.forEach(file => {
                    formData.append('attachments[]', file)
                })
                this.restPostWithFile(this.sharedDocumentUrl + "/attachment/validate", {}, formData,
                    () => {
                        this.submitDocument()
                    },
                    () => {
                        this.showAlert(this.$t('general_guides.invalid_file_type'))
                        this.loading = false
                    }
                )
            } else {
                this.submitDocument()
            }
        },

        submitDocument: function() {
            this.loading = true
            if (this.documentUpdate.id > 0) {
                this.restUpdate(this.sharedDocumentUrl, this.documentUpdate, this.updateSuccess, this.updateFail)
            } else {
                this.restAdd(this.sharedDocumentUrl, this.documentUpdate, this.uploadSuccess, this.uploadFail)
            }

        },

        updateSuccess: function () {
            this.loading = false
            this.$emit('close')
        },

        updateFail: function () {
            this.showAlert(this.$t('common.alert_update_failed'))
            this.loading = false
        },

        uploadSuccess: function (data) {
            this.status = data.id
            this.uploadAttachment(data.id)
        },

        uploadFail: function () {
            this.showAlert(this.$t('common.alert_add_failed'))
            this.loading = false
        },

        uploadAttachment: function (id) {
            let formData = new FormData()
            this.attachments.forEach(file => {
                formData.append('attachments[]', file)
            })
            this.restPostWithFile(this.sharedDocumentUrl + "/attachment/" + id, {}, formData,
                () => {
                    this.loading = false
                    this.$emit('close')
                },
                () => {
                    this.loading = false
                    this.showAlert(this.$t('general_guides.upload_failed'))
                }
            )
        },

        deleteSharedDocument: function (id) {
            this.loading = true
            this.restDelete(this.sharedDocumentUrl + "/attachment/" + id, this.deleteSuccess, this.deleteFail)
        },

        confirmDeleteSharedDocument: function () {
            if (!this.loading) {
                this.deletion = true
            }
        },

        deleteSuccess: function () {
            this.loading = false
            this.$emit('close')
        },

        deleteFail: function () {
            this.showAlert(this.$t('common.alert_update_failed'))
            this.loading = false
        },

        downloadAttachment: function (document) {
            this.loading = true
            this.restFetch(this.sharedDocumentUrl + "/attachment/" + document.id + "/" + document.attachment.id,
                response => {
                    this.saveFile(document.attachment.file_original_name, response.data)
                    this.loading = false
                }, () => {
                    this.loading = false
                    this.showAlert(this.$t('common.download_failed'))
                })
        },
    }
}
</script>

